// @flow
import * as React from 'react';
import { getCatalogUrl, getSellerUrl } from '../../utils/urls';
import Address from '@liveauctioneers/caterwaul-components/lib/Address/Address';
import BidConsoleOpener from '../../components/BidConsoleOpener/BidConsoleOpener';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import ButtonLink from '@liveauctioneers/caterwaul-components/lib/ButtonLink/ButtonLink';
import { type Catalog } from '../../types/Catalog';
import { catalogCardDate } from '../../utils/dates';
import CatalogDate from '../../components/CatalogDate/CatalogDate';
import classNames from 'classnames';
import ConsoleSocial from '../../components/ConsoleSocial/ConsoleSocial';
import CoverLotsComponent from '../../components/CoverLots/CoverLotsComponent';
import Flag from '@liveauctioneers/caterwaul-components/lib/Intl/Flag';
import FollowSellerContainer from '../../components/FollowSeller/FollowSellerContainer';
import { FormattedMessage } from 'react-intl';
import { getAddressForCatalog } from '../../utils/address';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import noop from '../../utils/noop';
import OverlayTrigger from '../../components/Overlay/OverlayTrigger';
import Popover from 'react-bootstrap/lib/Popover';
import RegisterLinkContainer from '../../components/RegisterLink/RegisterLinkContainer';
import { safelyRender } from '../../utils/safelyRender';
import type { Seller } from '@liveauctioneers/flow-types/lib/Seller';
import ShouldLogInContainer from '../../components/ShouldLogIn/ShouldLogInContainer';
import { showCountDownBasedOnMinimumDate } from '../../utils/countDown';
import styles from './CatalogCard.module.scss';

type Props = {
    bidders: number,
    catalog: Catalog,
    className: string,
    coverLots: any,
    featured: boolean,
    followed?: boolean,
    hasVideo: boolean,
    horizontal: boolean,
    percentComplete: number,
    preview: boolean,
    seller: Seller,
    showLive: boolean,
    showSendSellerConsignmentMessageModal?: Function,
    skipVisibilityCheck: boolean,
    style: any,
    trackClick: Function,
    trending: boolean,
};

const limitDescription = (description: string) => {
    if (description && description.length > 139) {
        description = `${description.slice(0, 139).trim()}…`;
    }
    return safelyRender(description);
};

const CatalogCard = (props: Props) => {
    const {
        bidders = 0,
        catalog,
        className = '',
        coverLots,
        featured = false,
        followed,
        hasVideo,
        horizontal = false,
        percentComplete,
        preview = false,
        seller,
        showLive,
        showSendSellerConsignmentMessageModal,
        skipVisibilityCheck,
        style = {},
        trackClick = noop,
        trending,
    } = props;

    const {
        catalogId,
        catalogStatus,
        description,
        imagePath,
        isCatalogOnly,
        isSEOExcluded,
        saleStartTs,
        sellerId,
        title,
    } = catalog || {};

    const { address1, address2, city, countryCode, postalCode, stateCode } = getAddressForCatalog(catalog, seller);

    const { name: sellerName } = seller || {};
    const startDate = catalogCardDate(saleStartTs);
    const catalogUrl = getCatalogUrl(catalogId, title);
    const showDate = !isCatalogOnly && showCountDownBasedOnMinimumDate(saleStartTs);
    const safeDescription = limitDescription(description);
    const safeTitle = safelyRender(title, 'span');
    const cantViewCatalogPopover = !catalogId && (
        <Popover id={catalogId} bsClass="popover">
            <FormattedMessage id="this_catalog_is_an_auction_preview" />
        </Popover>
    );

    const classKey = horizontal ? 'horizontal-catalog-card' : 'catalog-card';
    const classes = classNames(styles[classKey], className, styles['card-body']);
    const coverLotsClasses = featured ? classNames(styles.featured, styles['cover-lots']) : styles['cover-lots'];
    const consoleClass = featured ? classNames(styles.social, styles.featured) : styles.social;
    const coverLotsBlockClasses = horizontal ? styles.left : '';
    const titleStyles = horizontal ? styles.title : classNames(styles.left, styles.title);
    const infoStyles = horizontal ? styles.middle : '';
    const sellerStyles = classNames(styles.left, styles.seller);
    const timeStyles = horizontal ? styles.right : styles.left;
    const showLiveStyles = horizontal
        ? ''
        : featured
        ? classNames(styles.right, styles.bottom)
        : classNames(styles.right, styles.live);

    let innerClass;
    if (horizontal) {
        innerClass = styles['info-box'];
    } else if (featured) {
        innerClass = classNames(styles['card-body'], styles['expanded-preview']);
    } else {
        innerClass = styles['card-body'];
    }

    const location = [city, stateCode].filter(element => element).join(', ');

    const progressStyle = {
        width: `${percentComplete}%`,
    };
    const linkRel = isSEOExcluded ? 'nofollow' : '';
    return (
        <div className={classes} onClick={trackClick} style={style}>
            <div className={coverLotsBlockClasses}>
                {preview ? (
                    <div className={styles['preview-image-container']}>
                        <ImageWithFallback
                            skipVisibilityCheck={skipVisibilityCheck}
                            className={styles.image}
                            src={imagePath}
                            alt="the image"
                        />
                    </div>
                ) : (
                    <CoverLotsComponent
                        catalogId={catalogId}
                        catalogUrl={catalogUrl}
                        className={coverLotsClasses}
                        coverLots={coverLots}
                        featured={featured}
                        isSEOExcluded={isSEOExcluded}
                        sellerId={sellerId}
                        skipVisibilityCheck={skipVisibilityCheck}
                    />
                )}
                {showLive && !horizontal && (
                    <>
                        <ConsoleSocial
                            bidders={bidders}
                            className={consoleClass}
                            isOnCatalogCard
                            trending={trending}
                            hasVideo={hasVideo}
                        />
                        <div className={styles['bar-container']}>
                            <div className={styles.bar} style={progressStyle} />
                        </div>
                    </>
                )}
            </div>
            <div className={innerClass}>
                <div className={infoStyles}>
                    <span className={titleStyles}>
                        {preview ? (
                            horizontal ? (
                                safeTitle
                            ) : (
                                <OverlayTrigger
                                    trigger={['click']}
                                    placement="top"
                                    overlay={cantViewCatalogPopover}
                                    rootClose
                                >
                                    {safeTitle}
                                </OverlayTrigger>
                            )
                        ) : (
                            <>
                                <Link
                                    route
                                    className={featured ? styles['inner-title'] : ''}
                                    rel={linkRel}
                                    to={catalogUrl}
                                    primary
                                >
                                    {safeTitle}
                                </Link>
                                {featured && (
                                    <ButtonLink url={catalogUrl} rel={linkRel} className={styles.explore}>
                                        <FormattedMessage id="explore" />
                                    </ButtonLink>
                                )}
                            </>
                        )}
                    </span>
                    <span className={styles.description}>
                        {horizontal &&
                            (preview ? (
                                <span>{safeDescription}</span>
                            ) : (
                                <Link route to={catalogUrl} rel={linkRel} primaryWeak>
                                    {safeDescription}
                                </Link>
                            ))}
                    </span>
                </div>
                {!horizontal && (
                    <div className={sellerStyles}>
                        <Link route to={getSellerUrl(sellerId, sellerName)} primary>
                            {sellerName}
                        </Link>
                    </div>
                )}
                <div className={timeStyles}>
                    <div className={styles['start-date']}>
                        {showLive || catalogStatus === 'done' ? (
                            <FormattedMessage id="started" values={{ startDate }} />
                        ) : (
                            <>
                                <FormattedMessage id="starts" values={{ startDate }} />
                                {horizontal && !preview && (
                                    <Address
                                        address={{
                                            address1,
                                            address2,
                                            city,
                                            countryCode,
                                            postalCode,
                                            state: stateCode,
                                        }}
                                        cityState
                                        className={styles.address}
                                    />
                                )}
                            </>
                        )}
                    </div>
                    {horizontal && preview && (
                        <>
                            <FollowSellerContainer link sellerId={sellerId}>
                                <Button primary>
                                    {followed ? (
                                        <FormattedMessage id="following_updates" />
                                    ) : (
                                        <FormattedMessage id="follow_updates" />
                                    )}
                                </Button>
                            </FollowSellerContainer>
                            <div className={styles.consign}>
                                <ShouldLogInContainer
                                    requireLogin
                                    requireCompleteProfile
                                    runFunction={showSendSellerConsignmentMessageModal}
                                >
                                    <Link>
                                        <FormattedMessage id="consign_item" />
                                    </Link>
                                </ShouldLogInContainer>
                            </div>
                            <OverlayTrigger
                                trigger={['click', 'hover']}
                                placement="top"
                                overlay={cantViewCatalogPopover}
                                rootClose
                            >
                                <div className={styles['cant-view']}>
                                    <FormattedMessage id="why_cant_i_view_the_catalog" />
                                </div>
                            </OverlayTrigger>
                        </>
                    )}
                    {!horizontal && !featured && !preview && (
                        <>
                            <Flag className={styles.flag} countryCode={countryCode} />
                            <span className={styles.location}>{` ${location}`}</span>
                        </>
                    )}
                    {preview && !horizontal && (
                        <span className={showLiveStyles}>
                            <FollowSellerContainer sellerId={sellerId}>
                                <Link className={styles['follow-container']}>
                                    {followed ? (
                                        <FormattedMessage id="following_updates" />
                                    ) : (
                                        <FormattedMessage id="follow_updates" />
                                    )}
                                </Link>
                            </FollowSellerContainer>
                        </span>
                    )}
                    {!preview && (
                        <div className={showLiveStyles}>
                            {horizontal ? (
                                showLive ? (
                                    <div className={styles['live-link-container']}>
                                        <BidConsoleOpener catalogId={catalogId} sellerId={sellerId}>
                                            <span className={styles.live}>
                                                <FormattedMessage id="live_auction" />
                                            </span>
                                        </BidConsoleOpener>
                                    </div>
                                ) : (
                                    !isCatalogOnly && <CatalogDate className={styles.countdown} date={saleStartTs} />
                                )
                            ) : showLive ? (
                                <BidConsoleOpener catalogId={catalogId} sellerId={sellerId}>
                                    <span className={styles.live}>
                                        <FormattedMessage id="live_auction" />
                                        &nbsp;&nbsp;
                                    </span>
                                </BidConsoleOpener>
                            ) : (
                                <>
                                    {catalogStatus !== 'done' ? (
                                        <CatalogDate
                                            className={styles.countdown}
                                            date={saleStartTs}
                                            showDate={showDate}
                                        />
                                    ) : (
                                        <span className={styles.ended}>
                                            <FormattedMessage id="auction_ended" />
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                    {horizontal && !preview && (
                        <ButtonLink url={catalogUrl} className={styles.explore}>
                            <FormattedMessage id="explore" />
                        </ButtonLink>
                    )}
                    {horizontal && !preview && (
                        <RegisterLinkContainer
                            className={styles['register-link']}
                            catalogId={catalogId}
                            isCatalogOnly={isCatalogOnly}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default React.memo<Props>(CatalogCard, isEqual);
